import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";
import { cn } from "@/shared/lib/cn";

type LinkVariant = "default" | "muted" | "button" | "buttonSecondary";

interface BaseLinkProps {
  variant?: LinkVariant;
  children: ReactNode;
  className?: string;
}

interface ExternalLinkProps
  extends BaseLinkProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "className"> {
  /** Külső URL (http://, https://, mailto:, stb.) – ha üres, span-ként rendereli */
  href: string | undefined;
  /** Belső útvonal - NE használd külső linkekhez */
  to?: never;
  /** Új lapon nyíljon meg (alapértelmezett: true külső linkeknél) */
  external?: boolean;
}

interface InternalLinkProps extends BaseLinkProps, Omit<RouterLinkProps, "children" | "className"> {
  /** Külső URL - NE használd belső linkekhez */
  href?: never;
  /** Belső útvonal (pl. /login, /register) */
  to: string;
  external?: never;
}

export type LinkProps = ExternalLinkProps | InternalLinkProps;

/**
 * Link komponens belső és külső linkekhez.
 *
 * Használat:
 * ```tsx
 * // Belső link (React Router)
 * <Link to="/login">Bejelentkezés</Link>
 *
 * // Külső link (új lapon nyílik)
 * <Link href="https://example.com">Külső oldal</Link>
 *
 * // Külső link ugyanazon a lapon
 * <Link href="https://example.com" external={false}>Külső oldal</Link>
 *
 * // Halvány stílus
 * <Link to="/home" variant="muted">Vissza</Link>
 *
 * // Button stílusú link (szemantikusan <a>, vizuálisan button)
 * <Link to="/login" variant="button">Bejelentkezés</Link>
 * <Link to="/register" variant="buttonSecondary">Regisztráció</Link>
 * ```
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ variant = "default", className, children, ...props }, ref) => {
    const isButtonVariant = variant === "button" || variant === "buttonSecondary";

    const linkClasses = cn(
      "cursor-pointer transition-colors",
      variant === "default" && "text-brand-primary font-medium hover:underline",
      variant === "muted" && "text-gray-500 hover:text-gray-700",
      isButtonVariant && [
        "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-base font-semibold no-underline",
        "transition-all duration-300",
        "focus-visible:ring-brand-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
      ],
      variant === "button" && [
        "bg-gradient-brand text-white shadow-button",
        "hover:bg-gradient-brand-hover hover:shadow-lg hover:no-underline",
      ],
      variant === "buttonSecondary" && [
        "border-brand-primary text-brand-primary border-2 bg-white",
        "hover:bg-brand-primary hover:text-white hover:no-underline",
      ],
      className
    );

    // Belső link (React Router)
    if ("to" in props && props.to) {
      const { to, ...rest } = props;
      return (
        <RouterLink ref={ref} className={linkClasses} to={to} {...rest}>
          {children}
        </RouterLink>
      );
    }

    // Külső link
    if ("href" in props && props.href) {
      const { href, external = true, ...rest } = props;
      return (
        <a
          ref={ref}
          className={linkClasses}
          href={href}
          {...(external && {
            target: "_blank",
            rel: "noopener noreferrer",
          })}
          {...rest}
        >
          {children}
        </a>
      );
    }

    // Fallback (nem szabadna ide jutni)
    return (
      <span ref={ref} className={linkClasses}>
        {children}
      </span>
    );
  }
);

Link.displayName = "Link";
